import { FC, useCallback, useRef } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import {
    PdpRespProductTileCarousel,
    PdpRespProductTileCarouselProps,
} from './PdpRespProductTileCarousel/PdpRespProductTileCarousel';

import {
    trackSellerPageClick,
    trackSellerItemClick,
    PDP_MORE_FROM_SELLER_LIST,
    trackCarouselItemImpressions,
} from '../tracking/similarItemsTracking';
import { flattenConnectionNodes } from '../../utils/relayUtils';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { usePaginationHooks } from './util';

import { PdpRespSellerItemsCarousel_viewer$key } from './__generated__/PdpRespSellerItemsCarousel_viewer.graphql';
import { PdpRespSellerItemsCarousel_item$key } from './__generated__/PdpRespSellerItemsCarousel_item.graphql';
import { PdpRespSellerItemsCarousel_user$key } from './__generated__/PdpRespSellerItemsCarousel_user.graphql';

type Props = {
    viewer: PdpRespSellerItemsCarousel_viewer$key;
    item: PdpRespSellerItemsCarousel_item$key;
    user: PdpRespSellerItemsCarousel_user$key | null;
    currency: string;
    trackingLocation: string;
};

const carouselTitle = (
    <FormattedMessage
        defaultMessage="More From This Seller"
        id="PdpRespSellerItemsCarousel.title"
    />
);

const viewerFragment = graphql`
    fragment PdpRespSellerItemsCarousel_viewer on Viewer
    @refetchable(queryName: "PdpRespSellerItemsCarouselRefetchQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 6 }
        cursor: { type: "String" }
        userCountryCode: { type: "String" }
        priceBookName: { type: "String" }
    ) {
        ...PdpRespProductTileCarousel_viewer
        sellerItems: moreLikeThisItem(
            first: $count
            after: $cursor
            seller: INCLUDE
            itemId: $itemId
        ) @connection(key: "PdpRespSellerItemsCarousel_sellerItems", filters: []) {
            edges {
                node {
                    ...PdpRespProductTileCarousel_items
                        @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
                }
            }
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespSellerItemsCarousel_user on User {
        ...PdpRespProductTileCarousel_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSellerItemsCarousel_item on Item {
        serviceId
        seller {
            serviceId
            isAccessible
        }
    }
`;

export const PdpRespSellerItemsCarousel: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    currency,
    trackingLocation,
}) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { onClickArrow, onCarouselReset } = usePaginationHooks(loadNext, {
        label: PDP_MORE_FROM_SELLER_LIST,
    });
    const impressionTrackingRef = useRef({});
    const { serviceId: itemPk, seller } = item || {};
    const { serviceId: sellerPk, isAccessible } = seller || {};

    const sellerItems = flattenConnectionNodes(viewer?.sellerItems);
    const hasSellerItems = sellerItems.length;

    const onItemsVisible = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onItemsVisible']>
    >(
        (visibleItems, startIndex) =>
            trackCarouselItemImpressions({
                visibleItems,
                startIndex,
                impressionTrackingRef,
                list: trackingLocation,
            }),
        [trackingLocation]
    );

    if (!hasSellerItems || !isAccessible) {
        return null;
    }

    return (
        <PdpRespProductTileCarousel
            viewer={viewer}
            items={sellerItems}
            user={user}
            currency={currency}
            title={carouselTitle}
            location={locations.SELLER_GRID}
            viewAllLinkData={{
                path: `/more-from-seller/?itemPk=${itemPk}&sellerPk=${sellerPk}`,
                isLinkable: false,
            }}
            onClickViewAll={trackSellerPageClick}
            onClickArrow={onClickArrow}
            onClickItemTile={(similarItem, index) =>
                trackSellerItemClick(index, { node: similarItem }, trackingLocation)
            }
            onItemsVisible={onItemsVisible}
            onCarouselReset={onCarouselReset}
            dataTn="pdp-more-from-this-seller-grid"
        />
    );
};
